import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { refreshCategories } from '../../hooks/useCategories';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } })
    .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

const KINDS = [
  { key: 'material', label: 'Material Categories', color: '#065f46', hint: 'Used on Material Quotes and the Material Database' },
  { key: 'sub', label: 'Submittal (Sub) Categories', color: '#1e3a5f', hint: 'Used on Submittals' },
  // The drills (Pat, Sep 25 2026). Their order here is the order of the lanes on the
  // drilling boards. Owner, Executives and the office change these; PMs pick from them.
  { key: 'vertical_drill', label: 'Vertical Drills', color: '#1d4ed8', hint: 'The Vertical Drilling cards and board pick from this list, in this order' },
  { key: 'directional_drill', label: 'Directional Drills', color: '#7c3aed', hint: 'The Directional Drilling cards and board pick from this list, in this order' },
];

export default function AdminCategories() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState({});   // kind -> draft name
  const [editing, setEditing] = useState(null); // { id, name }
  const [busy, setBusy] = useState(false);

  function load() {
    apiFetch('/categories?all=1').then(setCats).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);
  const after = list => { setCats(list); refreshCategories(); };

  async function add(kind) {
    const name = (adding[kind] || '').trim();
    if (!name) return;
    setBusy(true);
    try {
      const c = await apiFetch('/categories', { method: 'POST', body: JSON.stringify({ name, kind }) });
      after([...cats, c]); setAdding(a => ({ ...a, [kind]: '' }));
    } catch (e) { alert(e.error || 'Could not add'); }
    setBusy(false);
  }
  async function rename(c, name) {
    if (!name.trim() || name === c.name) { setEditing(null); return; }
    try { const u = await apiFetch(`/categories/${c.id}`, { method: 'PATCH', body: JSON.stringify({ name }) }); after(cats.map(x => x.id === u.id ? u : x)); }
    catch (e) { alert(e.error || 'Rename failed'); }
    setEditing(null);
  }
  async function toggle(c) {
    try { const u = await apiFetch(`/categories/${c.id}`, { method: 'PATCH', body: JSON.stringify({ active: c.active ? 0 : 1 }) }); after(cats.map(x => x.id === u.id ? u : x)); } catch {}
  }
  // Move a row up or down: swap its place with the neighbour's.
  async function move(c, dir) {
    const list = cats.filter(x => x.kind === c.kind).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    const i = list.findIndex(x => x.id === c.id), j = i + dir;
    if (j < 0 || j >= list.length) return;
    const other = list[j];
    // Renumber the whole list so two rows never share a place.
    const order = list.slice(); order[i] = other; order[j] = c;
    try {
      const updated = await Promise.all(order.map((x, k) => (x.sort_order === k ? x : apiFetch(`/categories/${x.id}`, { method: 'PATCH', body: JSON.stringify({ sort_order: k }) }))));
      after(cats.map(x => updated.find(u => u.id === x.id) || x));
    } catch (e) { alert(e.error || 'Could not move it'); }
  }
  async function remove(c) {
    if (!window.confirm(`Delete "${c.name}"? Existing items already tagged with it keep the label.`)) return;
    try { await apiFetch(`/categories/${c.id}`, { method: 'DELETE' }); after(cats.filter(x => x.id !== c.id)); } catch {}
  }

  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13 };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <button onClick={() => navigate('/admin/settings')} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6 }}>‹ Settings</button>
          <h1 className="admin-page-title">Categories</h1>
          <p className="admin-page-sub">Manage the Material &amp; Submittal categories and the two drill lists — changes apply everywhere</p>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
          {KINDS.map(k => {
            const list = cats.filter(c => c.kind === k.key).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
            return (
              <div key={k.key} className="admin-card" style={{ overflow: 'hidden' }}>
                <div style={{ background: k.color, color: '#fff', padding: '12px 16px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{k.label}</div>
                  <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>{k.hint}</div>
                </div>
                <div style={{ padding: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {list.map(c => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: c.active ? '#fafafa' : '#f3f4f6', border: '1px solid #eef0f2', borderRadius: 8, opacity: c.active ? 1 : 0.6 }}>
                        {editing?.id === c.id ? (
                          <input autoFocus defaultValue={c.name} onBlur={e => rename(c, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') rename(c, e.target.value); if (e.key === 'Escape') setEditing(null); }} style={{ ...inp, flex: 1 }} />
                        ) : (
                          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: c.active ? '#111827' : '#9ca3af', textDecoration: c.active ? 'none' : 'line-through' }}>{c.name}</span>
                        )}
                        <button onClick={() => move(c, -1)} title="Move up" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', padding: '0 2px' }}>▲</button>
                        <button onClick={() => move(c, 1)} title="Move down" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', padding: '0 2px' }}>▼</button>
                        <button onClick={() => setEditing({ id: c.id })} title="Rename" style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Rename</button>
                        <button onClick={() => toggle(c)} title={c.active ? 'Retire' : 'Restore'} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{c.active ? 'Retire' : 'Restore'}</button>
                        <button onClick={() => remove(c)} title="Delete" style={{ background: 'none', border: 'none', color: '#d1d5db', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                    {list.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, padding: '6px 2px' }}>None yet.</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <input value={adding[k.key] || ''} onChange={e => setAdding(a => ({ ...a, [k.key]: e.target.value }))} onKeyDown={e => e.key === 'Enter' && add(k.key)}
                      placeholder="New category…" style={{ ...inp, flex: 1 }} />
                    <button onClick={() => add(k.key)} disabled={busy || !(adding[k.key] || '').trim()}
                      style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: k.color, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (adding[k.key] || '').trim() ? 1 : 0.5 }}>+ Add</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
